import URI from 'urijs';

import type { APIErrorType } from 'utils/errors';
import { APIError } from 'utils/errors';

import { getAppQueryClient } from './appQueryClientRef';

export const baseUrl = process.env.TQ_API_HOST ?? '';

const CSRF_ENDPOINT = '/api/csrf';
const CREDENTIALS: RequestCredentials = 'include';

export async function checkStatus(res: Response) {
  if (res.ok) {
    return res;
  }

  let payload: APIErrorType = {};

  try {
    const contentType = res.headers.get('content-type');
    if (contentType === 'text/html') {
      payload = {
        type: 'api_error',
        message: `${res.status} API Error`,
      };
    } else {
      payload = await res.json();
    }
  } catch (e) {
    // if we got a 500 error back, then there might not have been any JSON, so just return a
    // generic server error instead of a misleading 'Json Parsing Error'.  Callers are
    // expected to check for the 500 status code if they want to override and provide their
    // own contextually appropriate message to show to the user.
    if (res.status >= 500 && res.status <= 599) {
      payload = {
        type: 'server_error',
        message: `${res.status} Server Error`,
      };
    } else {
      payload = {
        type: 'parsing_error',
        message: `API Error: ${(e as any).toString()}`,
      };
    }
  }
  payload.status = res.status;
  throw new APIError(payload);
}

function redirectToLogin(): void {
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
}

function throwSessionExpired(): never {
  throw new APIError({
    type: 'session_expired',
    message: 'Your session has expired. Please log in again.',
    status: 401,
  });
}

async function fetchFreshCsrfToken(): Promise<string> {
  const res = await fetch(`${baseUrl}${CSRF_ENDPOINT}`, {
    credentials: CREDENTIALS,
    redirect: 'manual',
    headers: { Accept: 'application/json' },
  });
  if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) {
    redirectToLogin();
    throwSessionExpired();
  }
  if (!res.ok) {
    redirectToLogin();
    throwSessionExpired();
  }
  let data: { csrf?: string };
  try {
    data = await res.json();
  } catch {
    redirectToLogin();
    throwSessionExpired();
  }
  if (typeof data.csrf !== 'string') {
    redirectToLogin();
    throwSessionExpired();
  }
  getAppQueryClient()?.setQueryData('csrf', { csrf: data.csrf });
  return data.csrf;
}

async function responseIsCsrfFailure(res: Response): Promise<boolean> {
  if (res.status !== 403) {
    return false;
  }
  try {
    const body = await res.clone().json();
    return body?.type === 'csrf_failure';
  } catch {
    return false;
  }
}

async function fetchMutateWithCsrfRetry(
  initialToken: string,
  doFetch: (token: string) => Promise<Response>,
): Promise<Response> {
  let res = await doFetch(initialToken);
  if (await responseIsCsrfFailure(res)) {
    const fresh = await fetchFreshCsrfToken();
    res = await doFetch(fresh);
  }
  return res;
}

export async function getRequest<Data extends Record<string, unknown> | null, Response>(
  url: string,
  queryParams?: Data,
  opts: RequestInit = { headers: {} },
): Promise<Response> {
  let apiUrl: string | any = `${baseUrl}${url}`;
  if (queryParams) {
    apiUrl = new URI(apiUrl).addQuery(queryParams as object);
  }
  const res = await fetch(apiUrl, {
    credentials: CREDENTIALS,
    ...opts,
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/json',
      ...(opts.headers || {}),
    },
    method: 'GET',
  });
  await checkStatus(res);
  return res.json();
}

export async function postRequest<
  Data extends Record<string, unknown> | Array<Record<string, unknown>> | null,
  Response,
>(url: string, data: Data, csrfToken: string, signal?: AbortSignal, opts: RequestInit = {}): Promise<Response> {
  const res = await fetchMutateWithCsrfRetry(csrfToken, (token) =>
    fetch(`${baseUrl}${url}`, {
      credentials: CREDENTIALS,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
        'X-CSRFToken': token,
        ...(opts.headers ? opts.headers : {}),
      },
      method: 'POST',
      body: JSON.stringify(data),
      signal,
      ...opts,
    }),
  );
  if (res.status === 204) {
    return Promise.resolve(null as unknown as Response);
  }
  await checkStatus(res);
  // If the response is 'text/csv, then return the response as an arrayBuffer
  if (res.headers.get('Content-Type') === 'text/csv') {
    return (await res.arrayBuffer()) as unknown as Promise<Response>;
  }
  return res.json();
}

export async function patchRequest<Data extends Record<string, unknown> | Array<Record<string, unknown>>, Response>(
  url: string,
  data: Data,
  csrfToken: string,
  opts: RequestInit = { headers: {} },
): Promise<Response> {
  const res = await fetchMutateWithCsrfRetry(csrfToken, (token) =>
    fetch(`${baseUrl}${url}`, {
      credentials: CREDENTIALS,
      ...opts,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
        'X-CSRFToken': token,
        ...(opts.headers ? opts.headers : {}),
      },
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  );
  await checkStatus(res);
  if (res.status === 204) {
    return Promise.resolve(null as unknown as Response);
  }
  return res.json();
}

export async function putRequest<Data extends Record<string, unknown> | Array<Record<string, unknown>>, Response>(
  url: string,
  data: Data,
  csrfToken: string,
  opts: RequestInit = { headers: {} },
): Promise<Response> {
  const res = await fetchMutateWithCsrfRetry(csrfToken, (token) =>
    fetch(`${baseUrl}${url}`, {
      credentials: CREDENTIALS,
      ...opts,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
        'X-CSRFToken': token,
        ...(opts.headers ? opts.headers : {}),
      },
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  );
  await checkStatus(res);
  if (res.status === 204) {
    return Promise.resolve(null as unknown as Response);
  }
  return res.json();
}

export async function deleteRequest<Data extends Record<string, unknown> | null, Response>(
  url: string,
  data: Data,
  csrfToken: string,
  opts: RequestInit = { headers: {} },
): Promise<Response> {
  const res = await fetchMutateWithCsrfRetry(csrfToken, (token) =>
    fetch(`${baseUrl}${url}`, {
      credentials: CREDENTIALS,
      ...opts,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
        'X-CSRFToken': token,
        ...(opts.headers ? opts.headers : {}),
      },
      method: 'DELETE',
      body: JSON.stringify(data),
    }),
  );
  await checkStatus(res);
  if (res.status === 204) {
    return Promise.resolve(null as unknown as Response);
  }
  return res.json();
}
